/* ============================================
   WikiMermaidRenderer — diagramas Mermaid como SVG.
   ============================================ */

import { useEffect, useRef, useState } from 'react'
import mermaid from 'mermaid'
import { useTheme, type Tema } from '@/hooks/useTheme'

interface WikiMermaidRendererProps {
  chart: string
  id: string
}

/*
  Mermaid dibuja el SVG de una vez y le mete los colores adentro: no lee
  `var(--…)`, asi que aca los tokens no sirven y hay que darle hexes. Lo que si
  se puede es darle LOS DEL TEMA, y volver a inicializarlo cuando el tema
  cambia --que es lo que faltaba: los diagramas salian siempre oscuros, y en el
  tema claro quedaba un recuadro negro en medio de un articulo blanco.

  Los valores son los mismos tokens de `index.css`, copiados a mano porque es
  la unica forma de pasarselos.
*/
const PALETA: Record<Tema, Record<string, string>> = {
  dark: {
    primaryColor: '#1C4587', primaryTextColor: '#EAECF5', primaryBorderColor: '#4F7FE8',
    lineColor: '#8B8FA7', secondaryColor: '#242840', tertiaryColor: '#1C1F2E',
    background: '#1C1F2E', mainBkg: '#1C1F2E', nodeBorder: '#2A2E42',
    clusterBkg: '#161922', clusterBorder: '#2A2E42', titleColor: '#EAECF5',
    edgeLabelBackground: '#1C1F2E', actorTextColor: '#EAECF5', actorLineColor: '#4F7FE8',
    signalColor: '#8B8FA7', noteBkgColor: '#242840', noteTextColor: '#EAECF5',
    noteBorderColor: '#2A2E42',
  },
  light: {
    primaryColor: '#EEF1F8', primaryTextColor: '#141A2E', primaryBorderColor: '#2E63C8',
    lineColor: '#5A6485', secondaryColor: '#F4F6FB', tertiaryColor: '#FFFFFF',
    background: '#FFFFFF', mainBkg: '#FFFFFF', nodeBorder: '#D5DCEA',
    clusterBkg: '#F9FAFD', clusterBorder: '#E4E8F2', titleColor: '#141A2E',
    edgeLabelBackground: '#FFFFFF', actorTextColor: '#141A2E', actorLineColor: '#2E63C8',
    signalColor: '#5A6485', noteBkgColor: '#F4F6FB', noteTextColor: '#141A2E',
    noteBorderColor: '#E4E8F2',
  },
}

/* Se inicializa ANTES DE CADA dibujo, sin cachear. Mermaid guarda la config
   en un singleton del modulo, y el documento de la cotizacion lo reinicializa
   con SU paleta --siempre clara, porque se imprime--; con una cache aca, el
   primer diagrama de la Wiki despues de abrir una cotizacion saldria con los
   colores del documento. Inicializar es barato: es asignar un objeto. */
function initMermaid(tema: Tema) {
  mermaid.initialize({
    startOnLoad: false,
    theme: tema === 'dark' ? 'dark' : 'base',
    themeVariables: PALETA[tema],
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    securityLevel: 'strict',
  })
}

export default function WikiMermaidRenderer({ chart, id }: WikiMermaidRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [rendered, setRendered] = useState(false)
  const [tema] = useTheme()

  useEffect(() => {
    if (!chart.trim() || !containerRef.current) return

    initMermaid(tema)
    setError(null)
    setRendered(false)

    const elementId = `mermaid-${id}-${Date.now()}`

    const renderChart = async () => {
      try {
        const { svg } = await mermaid.render(elementId, chart.trim())
        if (containerRef.current) {
          containerRef.current.innerHTML = svg
          setRendered(true)
        }
      } catch (err: any) {
        console.error('Mermaid render error:', err)
        setError(err.message || 'Error renderizando diagrama')
        // Clean up any orphaned elements
        const orphan = document.getElementById(elementId)
        if (orphan) orphan.remove()
      }
    }

    renderChart()
  }, [chart, id, tema])

  if (error) {
    return (
      <div className="wiki-mermaid-error">
        <div className="wiki-mermaid-error__icon">
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div className="wiki-mermaid-error__text">
          <strong>Error en diagrama Mermaid</strong>
          <pre>{error}</pre>
        </div>
      </div>
    )
  }

  return (
    <div
      className="wiki-mermaid-container"
      ref={containerRef}
      style={{ opacity: rendered ? 1 : 0.3, transition: 'opacity 0.3s ease' }}
    />
  )
}
